import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { Kicker } from "./Kicker";
import { fadeIn } from "./fadeIn";
import { DOCUMENTED_INTEGRATION_COUNT } from "@/data/integrationCount";

/**
 * Alongside, not instead of. Huntbase reads the SIEM, EDR and AI SOC's data
 * where it sits and hands the evidence back. The logos are integrations and
 * are captioned as such: never customers.
 */
const TOOLS = [
  { name: "Your SIEM", job: "Holds what was logged." },
  { name: "Your EDR", job: "Works what fired on the endpoint." },
  { name: "Your AI SOC", job: "Triages the alerts that fire." },
];

/** A few of the documented integrations (logos served from content.huntbase.io, as on /integrations). */
const LOGO_BASE = "https://content.huntbase.io/products";
const LOGOS = [
  { name: "Splunk", file: "splunk_splunk.svg" },
  { name: "Microsoft Sentinel", file: "microsoft_azuresentinel.svg" },
  { name: "CrowdStrike Falcon", file: "crowdstrike_falcon.svg" },
  { name: "Elastic Security", file: "elastic-elastic.svg" },
  { name: "SentinelOne", file: "sentinelone_sentinelone.svg" },
  { name: "Okta", file: "okta_okta.svg" },
  { name: "Amazon AWS", file: "amazon_aws.svg" },
  { name: "Google Cloud Platform", file: "google_gcp.svg" },
];

export function AlongsideSection() {
  const reduce = useReducedMotion();
  return (
    <section className="bg-paper px-6 py-10 md:px-12 md:py-28" aria-labelledby="alongside-heading">
      <div className="mx-auto flex max-w-7xl flex-col gap-10 md:gap-12">
        <motion.div {...fadeIn(reduce)} className="flex max-w-3xl flex-col gap-4 md:gap-5">
          <Kicker>Alongside, not instead of</Kicker>
          <h2 id="alongside-heading" className="heading-lg text-ink text-balance">
            Your AI SOC works the alerts that fire. Huntbase answers the questions nothing fired
            for.
          </h2>
          <p className="hidden text-lg leading-relaxed text-ink-muted md:block">
            Keep the tools you trust. Huntbase reads their data where it sits and hands the evidence
            back as a hunt report, an export or over the API.
          </p>
        </motion.div>

        <motion.div
          {...fadeIn(reduce, 0.05)}
          className="grid items-stretch gap-3 md:grid-cols-[1fr_auto_1fr] md:gap-6"
        >
          <ul className="flex flex-col gap-2">
            {TOOLS.map((t) => (
              <li
                key={t.name}
                className="flex items-baseline justify-between gap-4 rounded-xl border border-rule bg-card px-4 py-2.5 md:py-3"
              >
                <span className="font-semibold text-ink">{t.name}</span>
                <span className="text-right text-sm text-ink-muted">{t.job}</span>
              </li>
            ))}
          </ul>
          <div className="hidden items-center text-primary-500 md:flex" aria-hidden>
            <ArrowRight size={28} weight="bold" />
          </div>
          <div className="flex flex-col justify-center gap-2 rounded-xl bg-primary-500 px-5 py-5 text-white md:px-7">
            <span className="text-lg font-semibold">Huntbase</span>
            <span className="text-[15px] leading-relaxed text-white/90">
              Answers &ldquo;were we hit?&rdquo; across all of them, asks the host for what none of
              them logged, and keeps checking.
            </span>
          </div>
        </motion.div>

        <motion.div
          {...fadeIn(reduce, 0.1)}
          className="flex flex-col gap-4 sm:border-t sm:border-rule sm:pt-6"
        >
          <p className="hidden text-sm text-ink-subtle sm:block">
            A few of the {DOCUMENTED_INTEGRATION_COUNT} integrations. These are integrations, not
            customers.
          </p>
          <ul
            className="hidden grid-cols-8 items-center gap-x-6 gap-y-5 sm:grid"
            aria-label="Example integrations"
          >
            {LOGOS.map((l) => (
              <li key={l.name} className="min-w-0">
                <img
                  src={`${LOGO_BASE}/${l.file}`}
                  alt={l.name}
                  title={l.name}
                  width={96}
                  height={28}
                  loading="lazy"
                  className="h-7 w-full object-contain opacity-80 grayscale"
                />
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold">
            <Link
              to="/integrations"
              className="group inline-flex items-center gap-1 text-primary-600 hover:text-primary-500"
            >
              All integrations
              <ArrowRight
                size={14}
                weight="bold"
                aria-hidden
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
            <Link
              to="/compare"
              className="group inline-flex items-center gap-1 text-primary-600 hover:text-primary-500"
            >
              How Huntbase fits with your tools
              <ArrowRight
                size={14}
                weight="bold"
                aria-hidden
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
